import {
  IAddon,
  createAddon,
  registerOperationHandler,
  createSingleItemOperationHandler,
  ISingleOperationPayload,
  IOperationHandlerThunkAction
} from '../../addons/registerOperationHandler';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { imageByUrlKeyFacet } from '../../models/images/ImageByUrlKeyFacet';
import { getItemsOperation, resolveItems } from '../../operations/getItems';
import { Signal } from '../../utilities/async/Signal';
import { EventGroup } from '@uifabric/utilities/lib/EventGroup';
import { Async } from '@uifabric/utilities/lib/Async';
import { openUrlFacet } from '../../facets/ItemFacets';
import { readConfiguration } from '../../operations/configuration/readConfiguration';
import { maxParallelImageLoadCountFacet } from '../../facets/ConfigurationFacets';
import { CancelError } from '../../utilities/async/CancelError';
import { wait } from '../../utilities/async/wait';
import { dimensionsFacet } from '../../facets/MediaFacets';

const IMAGE_LOAD_TIMEOUT = 30 * 1000; // 30 seconds
const IMAGE_DECODE_DELAY = 30; // 30 ms

function getImageByUrl(
  payload: ISingleOperationPayload<typeof imageByUrlKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    url
  } = imageByUrlKeyFacet.deserialize(payload.itemKey);

  return async (dispatch: IItemCacheDispatch) => {
    const {
      resolve,
      reject,
      promise
    } = new Signal<void>();

    const image = new Image();
    image.src = url;

    // Track whether or not this image was already decoded.
    const isAlreadyComplete = !!image.complete;

    const events = new EventGroup(null);
    const async = new Async(undefined);

    async.setTimeout(() => {
      // Set a timeout to avoid waiting too long for the image to load.
      // Otherwise the queue could starve if the image never fires an event.
      reject(new CancelError());
    }, IMAGE_LOAD_TIMEOUT);

    events.on(image, 'load', () => {
      resolve();
    });

    events.on(image, 'error', () => {
      reject(new Error('Image failed to load.'));
    });

    try {
      await promise;

      if (image.complete && !isAlreadyComplete) {
        // If the image decoded as a result of the load, wait a brief delay.
        // This prevents a deferred render in IE and Edge.
        await wait(IMAGE_DECODE_DELAY);
      }

      // TODO Add logic to extract error information encoded into the dimensions of a 1xN image,
      // but only if the image comes from a source which supports such behavior.

      dispatch(resolveItems({
        items: {
          [payload.itemKey]: {
            ...openUrlFacet.pack(url),
            ...dimensionsFacet.pack({
              width: image.naturalWidth || image.width || 0,
              height: image.naturalHeight || image.height || 0
            })
          }
        }
      }));
    } finally {
      async.dispose();
      events.dispose();
    }
  };
}

/**
 * Addon which manages the loading of images for optimized performance.
 */
export const imageAddon: IAddon = createAddon(() => {
  return (dispatch: IItemCacheDispatch) => {
    const {
      maxParallelImageLoadCount = 16
    } = dispatch(readConfiguration({
      maxParallelImageLoadCount: maxParallelImageLoadCountFacet
    }));

    dispatch(registerOperationHandler(imageByUrlKeyFacet, getItemsOperation)(
      createSingleItemOperationHandler({
        maxParallelOperationCount: maxParallelImageLoadCount
      })(getImageByUrl)
    ));
  };
});
